<template>
  <div>
    <button name="btnNotifications"
      class="button notification-button"
      :data-badge="$notifications.length > 0 ? $notifications.length : null"
      :class="[{ 'has-badge-danger has-badge-rounded has-badge-small': $notifications.length > 0 }]">
      <span class="msr msr-24px">notifications</span>
    </button>
    <tippy ref="tippyNotifications"
      placement="bottom"
      to="btnNotifications"
      arrow
      :interactive="true"
      :theme="`${$theme.name} bell-popover`"
      :max-width="500">
      <div class="container p-2 is-flex is-justify-content-space-between is-align-items-center">
        <span class="is-size-5">Notifications</span>
        <a v-if="$notifications.length > 0"
          class="has-text-link is-size-7"
          @click="dismissAll">
          Dismiss all
        </a>
      </div>
      <div class="is-divider mb-1 mt-0" />
      <div class="container notification-container">
        <div v-if="$notifications.length === 0"
          class="notification px-5 mb-0">
          <span>No new notifications </span>
        </div>
        <transition-group name="notification-fade"
          tag="div">
          <div v-for="(notification, index) in $notifications"
            :key="notification.id"
            class="notification-row">
            <hr v-if="index > 0"
              class="notification-divider my-0">
            <a v-if="notification.route"
              @click="openNotification(notification)">
              <div class="notification px-5 mb-0">
                <div class="is-flex is-align-items-center">
                  <span class="tag is-rounded is-medium mr-3 notification-icon-tag"
                    :class="(getNotificationTag(notification.type) || {}).classes">
                    <span class="icon">
                      <i :class="getIconCss(notification.type)"
                        aria-hidden="true" />
                    </span>
                  </span>
                  <div class="is-flex-grow-1 notification-body">
                    <span v-if="getNotificationTag(notification.type)"
                      class="tag is-rounded mb-1"
                      :class="getNotificationTag(notification.type).classes">
                      {{ getNotificationTag(notification.type).label }}
                    </span>
                    <div v-html="notification.content"
                      class="notification-content" />
                    <span v-if="notification.createdDate"
                      class="is-size-7 has-text-grey is-block mt-1">
                      {{ formatTimeAgo(notification.createdDate) }}
                    </span>
                  </div>
                  <button v-if="notification.type !== 3"
                    type="button"
                    class="button is-ghost is-small p-0 ml-3 notification-close"
                    aria-label="dismiss"
                    @click.stop.prevent="setAsRead(notification.id, notification.type)">
                    <span class="icon">
                      <i class="mdi mdi-close mdi-20px"
                        aria-hidden="true" />
                    </span>
                  </button>
                </div>
              </div>
            </a>
            <div v-else
              class="notification px-5 mb-0">
              <div class="is-flex is-align-items-center">
                <span class="tag is-rounded is-medium mr-3 notification-icon-tag"
                  :class="(getNotificationTag(notification.type) || {}).classes">
                  <span class="icon">
                    <i :class="getIconCss(notification.type)"
                      aria-hidden="true" />
                  </span>
                </span>
                <div class="is-flex-grow-1 notification-body">
                  <span v-if="getNotificationTag(notification.type)"
                    class="tag is-rounded mb-1"
                    :class="getNotificationTag(notification.type).classes">
                    {{ getNotificationTag(notification.type).label }}
                  </span>
                  <div v-html="notification.content"
                    class="notification-content" />
                  <span v-if="notification.createdDate"
                    class="is-size-7 has-text-grey is-block mt-1">
                    {{ formatTimeAgo(notification.createdDate) }}
                  </span>
                </div>
                <button v-if="notification.type !== 3 && notification.type !== 4"
                  type="button"
                  class="button is-ghost is-small p-0 ml-3 notification-close"
                  aria-label="dismiss"
                  @click.stop="setAsRead(notification.id, notification.type)">
                  <span class="icon">
                    <i class="mdi mdi-close mdi-20px"
                      aria-hidden="true" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </tippy>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { NotificationType } from '../../enums'

const TIME_AGO_REFRESH_MS = 30 * 1000

export default {
  name: 'Notification',
  components: {},
  mixins: [],
  props: {},
  data() {
    return { now: Date.now() }
  },
  computed: {},
  watch: {},
  created() {},
  mounted() {
    this._timeAgoTimer = setInterval(() => { this.now = Date.now() }, TIME_AGO_REFRESH_MS)
  },
  beforeDestroy() {
    if (this._timeAgoTimer) clearInterval(this._timeAgoTimer)
  },
  methods: {
    setAsRead(id, type) {
      // Optimistic — the store removes the row immediately and fires the
      // server call in the background, rolling back if it fails.
      this.dismissNotification({ id, type })
    },
    openNotification(notification) {
      // Dismiss immediately; the <transition-group> below plays a fade/collapse
      // leave animation so the row visually settles instead of vanishing.
      this.dismissNotification({ id: notification.id, type: notification.type })
      this.$router.push(notification.route)
    },
    formatTimeAgo(isoValue) {
      if (!isoValue) return ''
      // Treat the value as UTC because the backend writes DateTime.UtcNow and
      // serialised strings may arrive without an explicit 'Z'. Anchoring the
      // computation to this.now keeps it reactive while the dropdown is open.
      const dt = DateTime.fromISO(isoValue, { zone: 'utc' })
      if (!dt.isValid) return ''
      return dt.toRelative({ base: DateTime.fromMillis(this.now) }) || ''
    },
    dismissAll() {
      this.dismissAllNotifications()
    },
    getNotificationTag(type) {
      // label = visible text; classes = Bulma tag modifiers (no modifier => default grey).
      switch (type) {
        case NotificationType.QuoteCreated:
          return { label: 'New quote', classes: 'is-success is-light' }
        case NotificationType.QuoteImageUploadedIncoming:
          return { label: 'Customer upload', classes: 'is-primary' }
        case NotificationType.QuoteImageUploadedByUser:
          return { label: 'New images', classes: 'is-primary is-light' }
        case NotificationType.QuoteSmsIncoming:
          return { label: 'SMS', classes: 'is-info is-light' }
        case NotificationType.QuoteEmailIncoming:
          return { label: 'Email', classes: 'is-warning is-light' }
        case NotificationType.QuoteWhatsAppIncoming:
          return { label: 'WhatsApp', classes: 'is-success' }
        case NotificationType.CompanyLicense:
          return { label: 'License', classes: 'is-danger is-light' }
        default:
          return null
      }
    },
    getIconCss(type) {
      // Icon now lives inside a coloured tag — let it inherit the tag's text
      // colour instead of pinning it to icon-green / icon-white.
      return {
        mdi: true,
        'mdi-image': type == NotificationType.QuoteImageUploadedByUser,
        'mdi-account-arrow-up': type == NotificationType.QuoteImageUploadedIncoming,
        'mdi-whatsapp': type == NotificationType.QuoteWhatsAppIncoming,
        'mdi-message-text': type == NotificationType.QuoteSmsIncoming,
        'mdi-email': type == NotificationType.QuoteEmailIncoming,
        'mdi-license': type == NotificationType.CompanyLicense,
        'mdi-file-document-plus-outline': type == NotificationType.QuoteCreated,
        'mdi-24px': true
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>

<style lang="scss" scoped>
// Strip Bulma's notification background + severity/theme colour bleed so rows
// sit directly on the popover surface. Layout (padding, margins, sizing) is
// driven by Bulma utility classes on the markup — px-5, mb-0, ml-3, etc.
.notification {
  width: 30em;
  background-color: transparent !important;
  // Square corners so the row separator meets the popover edge cleanly.
  border-radius: 0 !important;
  transition: box-shadow 0.15s ease;

  &:hover {
    // Neutral grey overlay works on light + dark popovers without picking a fixed colour.
    box-shadow: inset 0 0 0 999px rgba(128, 128, 128, 0.15);
  }

  // Stacks the tag above the description and lets the wrapper shrink
  // properly inside the icon | body | close flex row.
  .notification-body {
    min-width: 0;
  }

  .notification-content {
    word-break: break-word;
  }

  // Square aspect on the icon-only Bulma tag so .is-rounded renders as a circle.
  .notification-icon-tag {
    width: 2.5em;
    height: 2.5em;
    padding: 0;
    flex-shrink: 0;
  }

  // .button.is-ghost gives a transparent button; we just override the default
  // underline-on-hover and tint the icon red on hover/focus.
  .notification-close {
    text-decoration: none !important;

    &:hover,
    &:focus-visible {
      color: #e53935 !important;
      background: transparent !important;
    }
  }
}
a .notification {
  cursor: pointer;
}

// <transition-group name="notification-fade"> leave animation. Each row fades
// out and collapses its height so dismissed notifications visually settle
// instead of vanishing; surviving rows slide up via the move transition.
.notification-row {
  overflow: hidden;
}
.notification-fade-leave-active {
  transition: opacity 0.25s ease, max-height 0.3s ease;
  max-height: 10rem;
}
.notification-fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.notification-fade-move {
  transition: transform 0.25s ease;
}

// Theme-aware foreground + separator colours. `.theme-light` / `.theme-dark`
// are set on <html> in App.vue.
.theme-light .notification,
.theme-light .notification .notification-content,
.theme-light .notification .notification-close {
  color: #1f2937 !important;
}
.theme-light .notification-divider {
  background-color: rgba(0, 0, 0, 0.35);
}
.theme-light .is-divider {
  border-top-color: rgba(0, 0, 0, 0.28) !important;
}

.theme-dark .notification,
.theme-dark .notification .notification-content,
.theme-dark .notification .notification-close {
  color: #f3f4f6 !important;
}
.theme-dark .notification-divider {
  // Lighter line so it's visible against the dark popover surface.
  background-color: rgba(255, 255, 255, 0.18);
}
.theme-dark .is-divider {
  // Override Bulma's --divider-background-color which doesn't reach the tippy
  // portal correctly here, leaving the header divider dark in dark mode.
  border-top-color: rgba(255, 255, 255, 0.16) !important;
}

.notification-container {
  max-height: 45vh;
  overflow-y: auto;
}

// Slim divider rendered between rows (skipped above the first row via v-if).
// Theme-aware colour is applied via `.theme-light` / `.theme-dark` overrides below.
.notification-divider {
  height: 1px;
  border: 0;
}

// Bell-icon wrapper — strip Bulma .button chrome since it's just an icon target.
.notification-button {
  border: none;
  background-color: transparent;
  box-shadow: none;
  padding: 0;

  &:hover {
    background-color: transparent !important;
    box-shadow: none !important;
  }
}
</style>

<!--
  Unscoped block: the tippy popover is portaled to document.body, so scoped
  styles can't reach it. The unique `bell-popover` theme keyword (added on
  the <tippy :theme="..."> binding above) keeps this rule limited to the
  notification bell rather than every tippy in the app.
-->
<style lang="scss">
// `.tippy-box[data-theme~='light']` in tippy.js's bundled CSS sets border: 0,
// which beats a single-class rule. Combine selectors + !important to win the
// cascade. The `bell-popover` keyword (added on the <tippy :theme="..."> above)
// scopes this to the notification dropdown only.
.theme-light .tippy-box[data-theme~='light'][data-theme~='bell-popover'] {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>